import { shouldForwardProp as sysShouldForwardProp } from '@mui/system';
import { FC, ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import Box, { BoxProps } from '@o2/ui/Box';
import { useLayoutUtilityClasses } from './layoutClasses';
import { BackgroundColorList } from '@o2/ui';

export interface LayoutContentProps extends BoxProps {
  children: ReactNode;
  'data-elm'?: string;

  /**
   * Footer: show footer content on bottom side (under page content)
   */
  footer?: ReactNode;

  /**
   * If true, the background of the page will be the internal color
   */
  internal?: boolean;

  /**
   * Sets background color from palette. If not set, default is used, can be overwritten by internal: true prop.
   */
  backgroundColor?: BackgroundColorList;
}

interface StyledContentOwnerState {
  internal?: boolean;
  backgroundColor?: BackgroundColorList;
}

const StyledContent = styled(Box, {
  name: 'Layout',
  slot: 'Content',
  shouldForwardProp: (prop) => sysShouldForwardProp(prop) && prop !== 'ownerState',
})<{ ownerState: StyledContentOwnerState }>(({ theme, ownerState: { internal, backgroundColor } }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  overflow: 'auto',
  order: 0,
  backgroundColor: internal
    ? theme.palette.background.internal
    : backgroundColor
    ? theme.palette.background[backgroundColor]
    : theme.palette.background.default,
  [theme.breakpoints.between('xs', 'md')]: {
    order: 1,
  },
}));

const LayoutContent: FC<LayoutContentProps> = ({
  children,
  internal = false,
  backgroundColor,
  footer,
  'data-elm': dataElm,
  ...others
}) => {
  const utilityClasses = useLayoutUtilityClasses();
  const ownerState = { internal, backgroundColor };

  return (
    <StyledContent ownerState={ownerState} className={utilityClasses.content} data-elm={dataElm} {...others}>
      <Box flexGrow={1} className={utilityClasses['content-box']}>
        {children}
      </Box>
      {footer}
    </StyledContent>
  );
};

export default LayoutContent;
